import { useState, useMemo, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { KPICard, StatusBadge } from './UI'
import { fmt, fmtFull, MONTHS_ORDER, CREATOR_COLORS, CREATOR_SOCIALS, DATE_ACTION_LABELS, getAgentCommission, isPaidStatus } from '../lib/constants'
import { useDialog } from '../lib/useDialog'
import { overdueInvoices as listOverdueInvoices } from '../lib/overdue'
import { fetchAgencyRecentWins } from '../lib/supabase'

// =============================================================================
// DASHBOARD.JS — Main dashboard with KPIs, charts, calendar, and creator view
// =============================================================================
// SECTIONS:
//   [UTILS]           timeAgo helper
//   [ACTIVITY_FEED]   Recent wins feed component
//   [ICONS]           SVG icon components (IG, TikTok, Link)
//   [CALENDAR]        DealCalendar + CalendarTooltip + DayPopup
//   [CREATOR_DASH]    CreatorDashboard (shown when logged in as creator)
//   [MAIN_DASH]       Main Dashboard export (admin/agent view with KPIs + charts)
// =============================================================================

// --- [UTILS] ---
function timeAgo(dateStr) {
  if (!dateStr) return ''
  const diff = (Date.now() - new Date(dateStr)) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

function ActivityFeed({ deals }) {
  const recent = useMemo(() =>
    [...deals].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 8)
  , [deals])

  return (
    <div style={{ background: 'var(--gradient-card)', border: '1px solid var(--card-border)', borderRadius: 'var(--radius-lg)', padding: 20, boxShadow: 'var(--shadow-sm)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, boxShadow: 'var(--shadow-inner)', borderRadius: 'var(--radius-lg)', pointerEvents: 'none' }} />
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, letterSpacing: '-0.3px' }}>Latest wins</div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 18 }}>New deals across the roster</div>
      {recent.length === 0 && <div style={{ color: 'var(--text3)', fontSize: 13 }}>No deals yet -- add the first one!</div>}
      {recent.map((deal, i) => (
        <div key={deal.id || i} className="pop-in row-hover" style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 10px', borderRadius: 8, marginBottom: 2,
          animationDelay: `${i * 0.05}s`, cursor: 'default'
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: (CREATOR_COLORS[deal.creator] || '#6b7280') + '1a',
            border: `1.5px solid ${CREATOR_COLORS[deal.creator] || '#6b7280'}`,
            color: CREATOR_COLORS[deal.creator] || '#6b7280',
            fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-display)', boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.08)'
          }}>
            {(deal.creator || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, lineHeight: 1.4 }}>
              <span style={{ fontWeight: 600, color: CREATOR_COLORS[deal.creator] || 'var(--text)' }}>{deal.creator}</span>
              <span style={{ color: 'var(--text2)' }}> just landed a deal with </span>
              <span style={{ fontWeight: 600 }}>{deal.brand}</span>
              {deal.agent && <span style={{ color: 'var(--text3)' }}> . via {deal.agent}</span>}
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>{timeAgo(deal.created_at)}</div>
        </div>
      ))}
    </div>
  )
}

// IG SVG icon
const IgIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" /><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>
)
// TikTok SVG icon
const TkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13.2a8.23 8.23 0 005.58 2.17V12a4.83 4.83 0 01-3.77-1.56V6.69h3.77z" /></svg>
)

// Link icon
const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" /></svg>
)

// Status color map for the visual board
const STATUS_COLORS = { Concept: '#6b7280', Production: '#3b82f6', Posting: '#8b5cf6', 'Partially Paid': '#f59e0b', 'Invoice Sent': '#ef4444', Paid: '#10b981', Done: '#10b981' }

function CalendarTooltip({ deal, children }) {
  const [pos, setPos] = useState(null)
  const label = DATE_ACTION_LABELS[deal.status]
  const line1 = `${deal.creator || ''} × ${deal.brand}`
  const line2 = `${deal.status}${label ? ` — ${label}` : ''}`
  const line3 = deal.deliverables || ''
  return (
    <div
      onMouseEnter={e => setPos({ x: e.clientX, y: e.clientY })}
      onMouseMove={e => setPos({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos && ReactDOM.createPortal(
        <div style={{
          position: 'fixed', left: pos.x + 12, top: pos.y - 10,
          padding: '10px 14px', background: '#1a1a2e', border: '1px solid var(--border)',
          borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.6)', zIndex: 9999,
          whiteSpace: 'nowrap', pointerEvents: 'none', minWidth: 140
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 3 }}>{line1}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)' }}>{line2}</div>
          {line3 && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{line3}</div>}
        </div>,
        document.body
      )}
    </div>
  )
}

function DayPopup({ dateStr, deals, onClose }) {
  const date = new Date(dateStr + 'T00:00')
  const label = date.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' })
  const dayDialog = useDialog(onClose, label)
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }} />
      <div {...dayDialog.props} className="pop-in" style={{ position: 'relative', width: 340, maxHeight: '60vh', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 14, fontFamily: 'var(--font-display)' }}>{label}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 18, cursor: 'pointer', padding: '0 4px' }}>×</button>
        </div>
        <div style={{ padding: '12px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {deals.map((deal, i) => {
            const actionLabel = DATE_ACTION_LABELS[deal.status]
            const statusColor = deal.status === 'Concept' || deal.status === 'In Production' ? '#8b5cf6'
              : deal.status === 'Approved' ? '#06b6d4'
              : deal.status === 'Invoice Sent' ? '#f97316' : '#6b7280'
            return (
              <div key={deal.id || i} style={{ padding: '10px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{deal.creator} × {deal.brand}</div>
                  <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 9, fontWeight: 700, background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}30` }}>{deal.status}</span>
                </div>
                {actionLabel && <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 2 }}>{actionLabel}</div>}
                {deal.deliverables && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{deal.deliverables}</div>}
              </div>
            )
          })}
        </div>
      </div>
    </div>,
    document.body
  )
}

function DealCalendar({ deals: calDeals, isMobile }) {
  const [calMonth, setCalMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1) })
  const [expandedDate, setExpandedDate] = useState(null)
  const year = calMonth.getFullYear()
  const month = calMonth.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const today = new Date()
  const todayStr = today.toISOString().slice(0, 10)
  const monthName = calMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

  const dealsByDate = {}
  calDeals.forEach(d => {
    if (!d.date_action) return
    if (!dealsByDate[d.date_action]) dealsByDate[d.date_action] = []
    dealsByDate[d.date_action].push(d)
  })

  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} style={{ minHeight: isMobile ? 55 : 80, background: 'var(--bg)', borderRadius: 6, opacity: 0.3 }} />)
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const dayDeals = dealsByDate[dateStr] || []
    const isToday = dateStr === todayStr
    const isPast = new Date(dateStr) < new Date(todayStr)
    cells.push(
      <div key={day} style={{
        minHeight: isMobile ? 55 : 80, background: isToday ? 'rgba(249,115,22,0.06)' : 'var(--bg2)',
        border: isToday ? '1.5px solid var(--accent)' : '1px solid var(--border)',
        borderRadius: 8, padding: '4px 5px', overflow: 'hidden'
      }}>
        <div style={{ fontSize: 10, fontWeight: isToday ? 700 : 500, color: isToday ? 'var(--accent)' : isPast ? 'var(--text3)' : 'var(--text2)', marginBottom: 3 }}>{day}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {dayDeals.slice(0, isMobile ? 2 : 3).map((deal, i) => {
            const statusColor = deal.status === 'Concept' || deal.status === 'In Production' ? '#8b5cf6'
              : deal.status === 'Approved' ? '#06b6d4'
              : deal.status === 'Invoice Sent' ? '#f97316' : '#6b7280'
            return (
              <CalendarTooltip key={deal.id || i} deal={deal}>
                <div style={{
                  fontSize: 9, fontWeight: 600, padding: '2px 4px', borderRadius: 4,
                  background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}30`,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3, cursor: 'default'
                }}>
                  {deal.brand}
                </div>
              </CalendarTooltip>
            )
          })}
          {dayDeals.length > (isMobile ? 2 : 3) && (
            <div onClick={(e) => { e.stopPropagation(); setExpandedDate(dateStr) }} style={{ fontSize: 8, color: 'var(--accent)', fontWeight: 600, padding: '1px 3px', cursor: 'pointer' }}>+{dayDeals.length - (isMobile ? 2 : 3)} more</div>
          )}
        </div>
        {expandedDate === dateStr && <DayPopup dateStr={dateStr} deals={dayDeals} onClose={() => setExpandedDate(null)} />}
      </div>
    )
  }

  const cardStyle = { background: 'var(--gradient-card)', border: '1px solid var(--card-border)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 16 : 20, boxShadow: 'var(--shadow-sm)', position: 'relative', overflow: 'hidden' }

  return (
    <div style={cardStyle}>
      <div style={{ position: 'absolute', inset: 0, boxShadow: 'var(--shadow-inner)', borderRadius: 'var(--radius-lg)', pointerEvents: 'none' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <button onClick={() => setCalMonth(new Date(year, month - 1, 1))} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', color: 'var(--text2)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>←</button>
        <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-display)', letterSpacing: '-0.3px' }}>{monthName}</div>
        <button onClick={() => setCalMonth(new Date(year, month + 1, 1))} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', color: 'var(--text2)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>→</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 3 }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', padding: '3px 0', letterSpacing: '0.5px' }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>{cells}</div>
      <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
        {[
          { label: 'Due Date', color: '#8b5cf6', desc: 'Concept / In Production' },
          { label: 'Post Date', color: '#06b6d4', desc: 'Approved' },
          { label: 'Sent On', color: '#f97316', desc: 'Invoice Sent' },
        ].map(l => (
          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--text3)' }}>
            <div style={{ width: 8, height: 8, borderRadius: 3, background: `${l.color}30`, border: `1px solid ${l.color}50` }} />
            <span style={{ fontWeight: 600, color: l.color }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function CreatorDashboard({ scopedDeals, currentUser, isMobile, creators, allAgencyDeals, privacyMode }) {
  const creatorName = currentUser?.agent_name || ''
  const color = CREATOR_COLORS[creatorName] || '#f97316'
  const socials = CREATOR_SOCIALS[creatorName] || {}
  const dbCreator = (creators || []).find(c => c.name === creatorName) || {}

  const stats = useMemo(() => {
    const total = scopedDeals.reduce((s, d) => s + (d.deal_amount || 0), 0)
    const paid = scopedDeals.filter(d => isPaidStatus(d.status)).reduce((s, d) => s + (d.deal_amount || 0), 0)
    const active = scopedDeals.filter(d => !isPaidStatus(d.status) && d.status !== 'Done').length
    const brands = [...new Set(scopedDeals.map(d => d.brand).filter(Boolean))]
    return { total, paid, totalDeals: scopedDeals.length, active, brands, paidPct: total > 0 ? (paid / total) * 100 : 0 }
  }, [scopedDeals])

  // Active deals grouped by status for kanban
  const STATUS_ORDER = ['Concept', 'In Production', 'Waiting For Approval', 'Approved', 'Posted', 'Invoice Sent', 'Partially Paid']
  const activeDealsGrouped = useMemo(() => {
    const active = scopedDeals.filter(d => !isPaidStatus(d.status) && d.status !== 'Done')
    const groups = {}
    active.forEach(d => {
      const s = d.status || 'Unknown'
      if (!groups[s]) groups[s] = []
      groups[s].push(d)
    })
    return STATUS_ORDER
      .filter(s => groups[s]?.length)
      .concat(Object.keys(groups).filter(s => !STATUS_ORDER.includes(s)))
      .filter(s => groups[s]?.length)
      .map(s => ({ status: s, deals: groups[s].sort((a, b) => (b.date || '').localeCompare(a.date || '')) }))
  }, [scopedDeals])

  // Recent wins — across the whole agency. A creator's login can only read
  // their own deals, so the agency-wide list comes from agency_recent_wins(),
  // which returns creator, brand and date and nothing else. Until it answers
  // (or if it fails) the creator's own deals stand in.
  const [agencyWins, setAgencyWins] = useState(null)
  useEffect(() => {
    let live = true
    fetchAgencyRecentWins(8).then(({ data, error }) => { if (live && !error && Array.isArray(data)) setAgencyWins(data) })
    return () => { live = false }
  }, [])
  const recentDeals = useMemo(() =>
    agencyWins && agencyWins.length
      ? agencyWins
      : [...(allAgencyDeals || scopedDeals)].sort((a, b) => (b.created_at || b.date || '').localeCompare(a.created_at || a.date || '')).slice(0, 8)
  , [agencyWins, allAgencyDeals, scopedDeals])

  const card = { background: 'var(--gradient-card)', border: '1px solid var(--card-border)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 16 : 20, boxShadow: 'var(--shadow-sm)', position: 'relative', overflow: 'hidden' }
  const innerGlow = { position: 'absolute', inset: 0, boxShadow: 'var(--shadow-inner)', borderRadius: 'var(--radius-lg)', pointerEvents: 'none' }
  const linkStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6,
    fontSize: 11, fontWeight: 600, textDecoration: 'none', border: '1px solid var(--border)',
    background: 'var(--bg3)', color: 'var(--text)', transition: 'all 0.15s'
  }

  // Reusable Recent Wins block
  const RecentWinsBlock = () => (
    <div style={{ ...card, padding: isMobile ? 18 : 24 }}>
      <div style={innerGlow} />
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, letterSpacing: '-0.3px' }}>Company wins</div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16 }}>Latest deals across the agency</div>
      {recentDeals.length === 0 && <div style={{ color: 'var(--text3)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>No deals yet!</div>}
      {recentDeals.map((deal, i) => {
        const dc = CREATOR_COLORS[deal.creator] || '#6b7280'
        return (
          <div key={deal.id || i} className="pop-in row-hover" style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 10px', borderRadius: 8, marginBottom: 2,
            animationDelay: `${i * 0.05}s`
          }}>
            <div style={{
              width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
              background: dc + '1a', border: `1.5px solid ${dc}`,
              color: dc, fontWeight: 700, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)'
            }}>
              {(deal.creator || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                <span style={{ fontWeight: 600, color: dc }}>{deal.creator}</span>
                <span style={{ color: 'var(--text2)' }}> landed </span>
                <span style={{ fontWeight: 600 }}>{deal.brand}</span>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>{timeAgo(deal.created_at)}</div>
          </div>
        )
      })}
    </div>
  )

  // Creator Card block
  const CreatorCardBlock = () => (
    <div style={{ ...card, borderTop: `3px solid ${color}`, padding: isMobile ? 18 : 24 }}>
      <div style={innerGlow} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${color}33, ${color}11)`, border: `2.5px solid ${color}`,
          color: color, fontWeight: 700, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-display)', boxShadow: `0 4px 16px ${color}22`
        }}>
          {(creatorName || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, letterSpacing: '-0.4px', color }}>{creatorName}</span>
            {socials.instagram && <a href={socials.instagram} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text3)', display: 'inline-flex' }}><IgIcon /></a>}
            {socials.tiktok && <a href={socials.tiktok} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text3)', display: 'inline-flex' }}><TkIcon /></a>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{stats.totalDeals} deals . {stats.active} active</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--accent)', letterSpacing: '-0.5px' }}>{privacyMode ? '•••••' : fmt(stats.total)}</div>
          <div style={{ fontSize: 10, color: 'var(--text3)' }}>total pipeline</div>
        </div>
      </div>
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text2)' }}>Collected</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#10b981' }}>{privacyMode ? '•••••' : fmt(stats.paid)}</span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: 'var(--bg)', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 3, background: 'linear-gradient(90deg, #10b981, #34d399)', width: `${Math.min(stats.paidPct, 100)}%`, transition: 'width 0.6s ease' }} />
        </div>
      </div>
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Links</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {socials.instagram && <a href={socials.instagram} target="_blank" rel="noopener noreferrer" style={{ ...linkStyle, borderColor: '#E1306C33', color: '#E1306C' }}><IgIcon /> IG</a>}
          {socials.tiktok && <a href={socials.tiktok} target="_blank" rel="noopener noreferrer" style={linkStyle}><TkIcon /> TT</a>}
          {dbCreator.beacons_url
            ? <a href={dbCreator.beacons_url} target="_blank" rel="noopener noreferrer" style={linkStyle}><LinkIcon /> Beacons</a>
            : <span style={{ ...linkStyle, opacity: 0.4, cursor: 'default' }}><LinkIcon /> Beacons</span>
          }
          {dbCreator.media_kit_url
            ? <a href={dbCreator.media_kit_url} target="_blank" rel="noopener noreferrer" style={linkStyle}><LinkIcon /> Media Kit</a>
            : <span style={{ ...linkStyle, opacity: 0.4, cursor: 'default' }}><LinkIcon /> Media Kit</span>
          }
        </div>
      </div>
      {stats.brands.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Brands</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {stats.brands.slice(0, 8).map(b => (
              <span key={b} style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: color + '12', border: `1px solid ${color}30`, color: color
              }}>{b}</span>
            ))}
            {stats.brands.length > 8 && <span style={{ fontSize: 11, color: 'var(--text3)', padding: '4px 6px' }}>+{stats.brands.length - 8}</span>}
          </div>
        </div>
      )}
    </div>
  )

  // Kanban block
  const KanbanBlock = () => stats.active > 0 ? (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 14, letterSpacing: '-0.3px' }}>Currently working on</div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(activeDealsGrouped.length, 4)}, 1fr)`, gap: 12 }}>
        {activeDealsGrouped.map(group => {
          const sc = STATUS_COLORS[group.status] || '#6b7280'
          return (
            <div key={group.status} style={{ ...card, padding: 0, borderTop: `3px solid ${sc}` }}>
              <div style={innerGlow} />
              <div style={{ padding: '14px 16px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: sc }} />
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{group.status}</span>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, background: 'var(--bg)', padding: '2px 8px', borderRadius: 10 }}>{group.deals.length}</span>
              </div>
              <div style={{ padding: '0 10px 10px' }}>
                {group.deals.map((deal, i) => {
                  const dateLabel = DATE_ACTION_LABELS[deal.status]
                  return (
                    <div key={deal.id || i} style={{
                      padding: '10px 12px', borderRadius: 8, marginBottom: 6,
                      background: 'var(--bg)', border: '1px solid var(--border)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: deal.deliverables ? 4 : 0 }}>{deal.brand}</div>
                          {deal.deliverables && <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.4 }}>{deal.deliverables}</div>}
                        </div>
                        {dateLabel && (
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{dateLabel}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: deal.date_action ? 'var(--text)' : '#f59e0b' }}>
                              {deal.date_action ? new Date(deal.date_action + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Not set'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  ) : null

  return (
    <div className="fade-in">
      {isMobile ? (
        <>
          <div style={{ marginBottom: 16 }}><CreatorCardBlock /></div>
          <KanbanBlock />
          <div style={{ marginBottom: 16 }}><DealCalendar deals={scopedDeals} isMobile={isMobile} /></div>
          <RecentWinsBlock />
        </>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <CreatorCardBlock />
            <RecentWinsBlock />
          </div>
          <KanbanBlock />
          <DealCalendar deals={scopedDeals} isMobile={isMobile} />
        </>
      )}
    </div>
  )
}

// ─── TODAY'S FOCUS WIDGET (admin only) ─────────────────────────────
// Pulls open work items from existing data — no DB changes.
function TodaysFocus({ deals, leads, isMobile, onJump }) {
  const focus = useMemo(() => {
    const now = new Date()
    const today = now.toISOString().slice(0, 10)
    const daysSince = (iso) => iso ? Math.floor((now.getTime() - new Date(iso).getTime()) / 86400000) : 0

    // 1. Follow-ups due (lead.next_follow_up <= today)
    const followUps = (leads || []).filter(l => l.next_follow_up && l.next_follow_up <= today && l.status !== 'Won' && l.status !== 'Lost')
      .sort((a, b) => (a.next_follow_up || '').localeCompare(b.next_follow_up || ''))

    // 2. Overdue invoices — past their actual due date, not a flat 14 days.
    //    Terms live on the deal (net 30 by default); see lib/overdue.js.
    const overdueInvoices = listOverdueInvoices(deals, now)

    // 3. Stuck approved deals (Approved + >5d in status)
    const stuckApproved = (deals || []).filter(d => d.status === 'Approved' && daysSince(d.status_updated_at || d.created_at || d.date) > 5)
      .map(d => ({ ...d, daysStuck: daysSince(d.status_updated_at || d.created_at || d.date) }))
      .sort((a, b) => b.daysStuck - a.daysStuck)

    // 4. Need invoicing (Posted, no invoice_url and no invoice_path)
    const needInvoice = (deals || []).filter(d => d.status === 'Posted' && !d.invoice_url && !d.invoice_path)
      .map(d => ({ ...d, daysSincePosted: daysSince(d.status_updated_at || d.created_at || d.date) }))
      .sort((a, b) => b.daysSincePosted - a.daysSincePosted)

    return { followUps, overdueInvoices, stuckApproved, needInvoice }
  }, [deals, leads])

  const total = focus.followUps.length + focus.overdueInvoices.length + focus.stuckApproved.length + focus.needInvoice.length

  const Row = ({ icon, color, label, count, items, jumpTo, formatItem }) => {
    if (count === 0) return null
    return (
      <div
        onClick={() => onJump && onJump(jumpTo)}
        style={{
          padding: '12px 14px',
          borderRadius: 10,
          background: 'var(--bg3)',
          border: `1px solid ${color}22`,
          cursor: onJump ? 'pointer' : 'default',
          transition: 'transform 0.1s, border-color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = color + '55'; e.currentTarget.style.transform = 'translateX(2px)' }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = color + '22'; e.currentTarget.style.transform = '' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{ fontSize: 18 }}>{icon}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color }}>{count} {label}</div>
              {items.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {items.slice(0, 3).map(formatItem).join('  •  ')}{items.length > 3 ? `  •  +${items.length - 3} more` : ''}
                </div>
              )}
            </div>
          </div>
          <div style={{ color: color, fontSize: 16, opacity: 0.6 }}>›</div>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      background: 'var(--gradient-card)',
      border: '1px solid var(--card-border)',
      borderRadius: 'var(--radius-lg)',
      padding: isMobile ? 16 : 22,
      marginBottom: 20,
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>📋</span>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, margin: 0 }}>Today's Focus</h2>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            {total === 0 ? 'Inbox zero. Nothing pressing right now.' : `${total} ${total === 1 ? 'item' : 'items'} needing attention`}
          </div>
        </div>
      </div>

      {total === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 12px', color: 'var(--text3)' }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>✨</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>All caught up</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>No overdue invoices, no stuck deals, no follow-ups due.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          <Row
            icon="🔔"
            color="#f97316"
            label={`follow-up${focus.followUps.length === 1 ? '' : 's'} due`}
            count={focus.followUps.length}
            items={focus.followUps}
            jumpTo="crm"
            formatItem={l => l.brand || l.client || 'Lead'}
          />
          <Row
            icon="💰"
            color="#ef4444"
            label={`invoice${focus.overdueInvoices.length === 1 ? '' : 's'} overdue`}
            count={focus.overdueInvoices.length}
            items={focus.overdueInvoices}
            jumpTo="reports"
            formatItem={d => `${d.brand} (+${d.daysOverdue}d)`}
          />
          <Row
            icon="⏰"
            color="#f59e0b"
            label={`approved deal${focus.stuckApproved.length === 1 ? '' : 's'} not yet posted`}
            count={focus.stuckApproved.length}
            items={focus.stuckApproved}
            jumpTo="deals"
            formatItem={d => `${d.creator} × ${d.brand}`}
          />
          <Row
            icon="📄"
            color="#3b82f6"
            label={`deal${focus.needInvoice.length === 1 ? '' : 's'} need${focus.needInvoice.length === 1 ? 's' : ''} invoicing`}
            count={focus.needInvoice.length}
            items={focus.needInvoice}
            jumpTo="deals"
            formatItem={d => `${d.creator} × ${d.brand}`}
          />
        </div>
      )}
    </div>
  )
}

export default function Dashboard({ deals, viewMode, currentUser, scopedDeals: sd, activeAgentName, isMobile, isCreator, creators, privacyMode, leads, isAdmin, setTab }) {
  const isAdminView = viewMode === 'admin'
  const agentName = activeAgentName || currentUser?.agent_name
  const [showIncomeBreakdown, setShowIncomeBreakdown] = useState(false)
  const incomeDialog = useDialog(() => setShowIncomeBreakdown(false), 'Income breakdown', true, showIncomeBreakdown)

  const scopedDeals = sd || deals

  // Creator gets a completely different dashboard
  if (isCreator) {
    return <CreatorDashboard scopedDeals={scopedDeals} currentUser={currentUser} isMobile={isMobile} creators={creators} allAgencyDeals={deals} privacyMode={privacyMode} />
  }

  const kpis = useMemo(() => {
    const total = scopedDeals.reduce((s, d) => s + (d.deal_amount || 0), 0)
    const paid = scopedDeals.filter(d => isPaidStatus(d.status)).reduce((s, d) => s + (d.deal_amount || 0), 0)
    const invoiced = scopedDeals.filter(d => d.status === 'Invoice Sent').reduce((s, d) => s + (d.deal_amount || 0), 0)
    // Company income = agency commissions from agent deals + Steve's personal deal revenue (no commission, full amount to agency)
    const steveComm = deals.reduce((s, d) => s + (d.commission_steve || 0), 0)
    // All of Steve's creator deals are personal revenue — full amount to the
    // agency, no commission split (they now carry agent='Steve', so don't
    // filter on a blank agent here).
    const stevePersonalRevenue = deals.filter(d => (d.creator || '').toLowerCase() === 'steve').reduce((s, d) => s + (d.deal_amount || 0), 0)
    const steveCreatorIncome = stevePersonalRevenue
    const companyIncome = steveComm + steveCreatorIncome
    // Agent commissions owed (Mirelle, Lydie, Loizza)
    const mirelleComm = deals.reduce((s, d) => s + (d.commission_mirelle || 0), 0)
    const lydieComm = deals.reduce((s, d) => s + (d.commission_lydie || 0), 0)
    const loizzaComm = deals.reduce((s, d) => s + (d.commission_loizza || 0), 0)
    const mirelleDeals = deals.filter(d => (d.agent || '').toLowerCase() === 'mirelle')
    const lydieDeals = deals.filter(d => (d.agent || '').toLowerCase() === 'lydie')
    const loizzaDeals = deals.filter(d => (d.agent || '').toLowerCase() === 'loizza')
    const mirelleRevenue = mirelleDeals.reduce((s, d) => s + (d.deal_amount || 0), 0)
    const lydieRevenue = lydieDeals.reduce((s, d) => s + (d.deal_amount || 0), 0)
    const loizzaRevenue = loizzaDeals.reduce((s, d) => s + (d.deal_amount || 0), 0)
    const mirelleToSteve = mirelleDeals.reduce((s, d) => s + (d.commission_steve || 0), 0)
    const lydieToSteve = lydieDeals.reduce((s, d) => s + (d.commission_steve || 0), 0)
    const loizzaToSteve = loizzaDeals.reduce((s, d) => s + (d.commission_steve || 0), 0)
    const mirelleTotalComm = mirelleDeals.reduce((s, d) => s + (d.commission_total || 0), 0)
    const lydieTotalComm = lydieDeals.reduce((s, d) => s + (d.commission_total || 0), 0)
    const loizzaTotalComm = loizzaDeals.reduce((s, d) => s + (d.commission_total || 0), 0)
    // Steve agenting for OTHER creators (20% to agency). His own creator deals
    // are personal revenue, not agent deals — they'd double-show in the
    // breakdown otherwise.
    const steveAgentDeals = deals.filter(d => (d.agent || '').toLowerCase() === 'steve' && (d.creator || '').toLowerCase() !== 'steve')
    const steveAgentRevenue = steveAgentDeals.reduce((s, d) => s + (d.deal_amount || 0), 0)
    const steveAgentTotalComm = steveAgentDeals.reduce((s, d) => s + (d.commission_total || 0), 0)
    const agentsOwed = mirelleComm + lydieComm + loizzaComm
    const totalCommission = deals.reduce((s, d) => s + (d.commission_total || 0), 0)
    const netIncome = companyIncome - agentsOwed
    const agentComm = scopedDeals.reduce((s, d) => s + getAgentCommission(d, agentName), 0)
    return { total, paid, invoiced, companyIncome, steveComm, steveCreatorIncome, agentsOwed, netIncome, agentComm, totalDeals: scopedDeals.length, totalCommission, mirelleComm, lydieComm, loizzaComm, mirelleRevenue, lydieRevenue, loizzaRevenue, mirelleToSteve, lydieToSteve, loizzaToSteve, mirelleTotalComm, lydieTotalComm, loizzaTotalComm, steveAgentRevenue, steveAgentTotalComm }
  }, [scopedDeals, deals, agentName])

  const monthlyData = useMemo(() => {
    const map = {}
    scopedDeals.forEach(d => {
      if (!d.month) return
      if (!map[d.month]) map[d.month] = { month: d.month.slice(0, 3), revenue: 0 }
      map[d.month].revenue += d.deal_amount || 0
    })
    return MONTHS_ORDER.filter(m => map[m]).map(m => map[m])
  }, [scopedDeals])

  const creatorData = useMemo(() => {
    const map = {}
    scopedDeals.forEach(d => {
      if (!d.creator) return
      if (!map[d.creator]) map[d.creator] = { creator: d.creator, revenue: 0 }
      map[d.creator].revenue += d.deal_amount || 0
    })
    return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, 6)
  }, [scopedDeals])

  const statusBreakdown = useMemo(() => {
    const map = {}
    scopedDeals.forEach(d => {
      const s = d.status || 'Unknown'
      if (!map[s]) map[s] = { status: s, value: 0, count: 0 }
      map[s].value += d.deal_amount || 0; map[s].count++
    })
    const PIPELINE_ORDER = ['Concept', 'In Production', 'Waiting For Approval', 'Approved', 'Posted', 'Invoice Sent', 'Partially Paid', 'Paid/Done']
    const rank = s => { const i = PIPELINE_ORDER.indexOf(s); return i === -1 ? PIPELINE_ORDER.length : i }
    return Object.values(map).sort((a, b) => rank(a.status) - rank(b.status))
  }, [scopedDeals])

  const TT = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    const creatorName = payload[0]?.payload?.creator
    const accentColor = creatorName ? (CREATOR_COLORS[creatorName] || 'var(--accent)') : 'var(--accent)'
    return (
      <div style={{ background: 'rgba(24,26,30,0.92)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '10px 16px', fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', borderLeft: `3px solid ${accentColor}`, minWidth: 90 }}>
        <div style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 4, fontSize: 11, fontWeight: 500, letterSpacing: '0.3px' }}>{label}</div>
        <div style={{ fontWeight: 700, color: accentColor, fontSize: 15 }}>{privacyMode ? '•••••' : fmtFull(payload[0].value)}</div>
      </div>
    )
  }

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 700, letterSpacing: '-0.6px' }}>
          {isAdminView ? 'Overview' : 'My Dashboard'}
        </h1>
        <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 3 }}>{kpis.totalDeals} deals . {new Date().getFullYear()}</p>
      </div>

      {/* Today's Focus — admin only */}
      {isAdmin && isAdminView && (
        <TodaysFocus deals={deals} leads={leads} isMobile={isMobile} onJump={setTab} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 10 : 14, marginBottom: 24 }}>
        <KPICard label="Total pipeline" value={privacyMode ? '•••••' : fmt(kpis.total)} sub={`${kpis.totalDeals} deals`} accent="#f97316" icon="$" />
        <KPICard label="Collected" value={privacyMode ? '•••••' : fmt(kpis.paid)} sub="Paid in full" accent="#10b981" icon="" />
        <KPICard label="Invoice sent" value={privacyMode ? '•••••' : fmt(kpis.invoiced)} sub="Awaiting payment" accent="#f59e0b" icon="" />
        {isAdminView
          ? <KPICard label="Company income" value={privacyMode ? '•••••' : fmt(kpis.netIncome)} sub={privacyMode ? 'After agent commissions' : 'Tap for breakdown'} accent="#3b82f6" icon="" onClick={() => !privacyMode && setShowIncomeBreakdown(true)} />
          : <KPICard label="My commissions" value={privacyMode ? '•••••' : fmt(kpis.agentComm)} sub="Your earned cut" accent="#8b5cf6" icon="" />
        }
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 10 : 14, marginBottom: 14 }}>
        <div style={{ background: 'var(--gradient-card)', border: '1px solid var(--card-border)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 14 : 20, boxShadow: 'var(--shadow-sm)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, boxShadow: 'var(--shadow-inner)', borderRadius: 'var(--radius-lg)', pointerEvents: 'none' }} />
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, letterSpacing: '-0.3px' }}>Monthly revenue</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 18 }}>All deals by month</div>
          <ResponsiveContainer width="100%" height={isMobile ? 120 : 150}>
            <BarChart data={monthlyData} barCategoryGap="30%">
              <defs>
                <linearGradient id="barActive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f97316" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#f97316" stopOpacity={0.4} />
                </linearGradient>
                <linearGradient id="barMuted" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2a2d35" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#2a2d35" stopOpacity={0.2} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" tick={{ fill: '#55575f', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<TT />} cursor={{ fill: 'rgba(255,255,255,0.04)', radius: 6 }} />
              <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                {monthlyData.map((_, i) => <Cell key={i} fill={i === monthlyData.length - 1 ? 'url(#barActive)' : 'url(#barMuted)'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background: 'var(--gradient-card)', border: '1px solid var(--card-border)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 14 : 20, boxShadow: 'var(--shadow-sm)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, boxShadow: 'var(--shadow-inner)', borderRadius: 'var(--radius-lg)', pointerEvents: 'none' }} />
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, letterSpacing: '-0.3px' }}>Top creators</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 18 }}>Revenue by creator</div>
          <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
            <BarChart data={creatorData} layout="vertical" barCategoryGap="20%">
              <defs>
                {creatorData.map((d, idx) => (
                  <linearGradient key={`cg-${idx}`} id={`creatorGrad-${idx}`} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={CREATOR_COLORS[d.creator] || '#3b82f6'} stopOpacity={0.85} />
                    <stop offset="100%" stopColor={CREATOR_COLORS[d.creator] || '#3b82f6'} stopOpacity={0.35} />
                  </linearGradient>
                ))}
              </defs>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="creator" tick={{ fill: '#8b8d96', fontSize: 11 }} axisLine={false} tickLine={false} width={72} />
              <Tooltip content={<TT />} cursor={{ fill: 'rgba(255,255,255,0.04)', radius: 6 }} />
              <Bar dataKey="revenue" radius={[0, 6, 6, 0]}>
                {creatorData.map((d, idx) => <Cell key={d.creator} fill={`url(#creatorGrad-${idx})`} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 10 : 14, marginBottom: 14 }}>
        <div style={{ background: 'var(--gradient-card)', border: '1px solid var(--card-border)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 14 : 20, boxShadow: 'var(--shadow-sm)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, boxShadow: 'var(--shadow-inner)', borderRadius: 'var(--radius-lg)', pointerEvents: 'none' }} />
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, letterSpacing: '-0.3px' }}>Pipeline by status</div>
          {statusBreakdown.map(({ status, value, count }) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StatusBadge status={status} />
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>{count}</span>
              </div>
              <span style={{ fontWeight: 700, fontSize: 13, fontFamily: 'var(--font-display)' }}>{privacyMode ? '•••••' : fmt(value)}</span>
            </div>
          ))}
        </div>
        <ActivityFeed deals={deals} />
      </div>

      <DealCalendar deals={scopedDeals} isMobile={isMobile} />

      {showIncomeBreakdown && !privacyMode && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={() => setShowIncomeBreakdown(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} />
          <div {...incomeDialog.props} className="pop-in" style={{
            position: 'relative', width: isMobile ? '92%' : 480, background: 'var(--bg)',
            border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
          }}>
            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.4px' }}>Company Income Breakdown</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{new Date().getFullYear()} overview</div>
              </div>
              <button onClick={() => setShowIncomeBreakdown(false)} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 20, cursor: 'pointer', padding: 4 }}>x</button>
            </div>

            <div style={{ padding: '20px 24px 24px' }}>
              {/* Net Income */}
              <div style={{ textAlign: 'center', marginBottom: 20, padding: '16px 0', background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Net Company Income</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#3b82f6', fontFamily: 'var(--font-display)', letterSpacing: '-0.5px' }}>{fmt(kpis.netIncome)}</div>
              </div>

              {/* Total Commission Generated */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Total Commission Generated (20% on $1K+ deals)</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13, color: 'var(--text2)' }}>All agents combined</span>
                  <span style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-display)', color: '#10b981' }}>{fmt(kpis.steveAgentTotalComm + kpis.mirelleTotalComm + kpis.lydieTotalComm + kpis.loizzaTotalComm)}</span>
                </div>
              </div>

              {/* Per-Agent Breakdown */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Agent Breakdown</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { name: 'Steve (agent)', color: '#3b82f6', revenue: kpis.steveAgentRevenue, comm: kpis.steveAgentTotalComm, cut: 0, agency: kpis.steveAgentTotalComm, rate: '20% to agency' },
                    { name: 'Mirelle', color: '#8b5cf6', revenue: kpis.mirelleRevenue, comm: kpis.mirelleTotalComm, cut: kpis.mirelleComm, agency: kpis.mirelleToSteve, rate: '6%' },
                    { name: 'Lydie', color: '#f59e0b', revenue: kpis.lydieRevenue, comm: kpis.lydieTotalComm, cut: kpis.lydieComm, agency: kpis.lydieToSteve, rate: 'hourly' },
                    { name: 'Loizza', color: '#ef4444', revenue: kpis.loizzaRevenue, comm: kpis.loizzaTotalComm, cut: kpis.loizzaComm, agency: kpis.loizzaToSteve, rate: '10%' },
                  ].map(a => (
                    <div key={a.name} style={{ padding: '12px 14px', background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: a.color }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{a.name}</span>
                        <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 'auto' }}>{a.rate}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                        <div style={{ padding: '8px 10px', background: 'var(--bg3)', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 3 }}>Generated</div>
                          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--text)' }}>{fmt(a.comm)}</div>
                        </div>
                        <div style={{ padding: '8px 10px', background: 'var(--bg3)', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 3 }}>Their Cut</div>
                          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-display)', color: a.cut > 0 ? a.color : 'var(--text3)' }}>{fmt(a.cut)}</div>
                        </div>
                        <div style={{ padding: '8px 10px', background: 'var(--bg3)', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 3 }}>For Agency</div>
                          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-display)', color: '#10b981' }}>{fmt(a.agency)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Steve Personal Deals */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Steve's Personal Deals (full amount to agency)</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13, color: 'var(--text2)' }}>Total deal revenue</span>
                  <span style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-display)', color: '#f97316' }}>{fmt(kpis.steveCreatorIncome)}</span>
                </div>
              </div>

              {/* Summary formula */}
              <div style={{ padding: '12px 14px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Agency commission (total)</span><span style={{ color: 'var(--text2)', fontWeight: 600 }}>{fmt(kpis.steveAgentTotalComm + kpis.mirelleTotalComm + kpis.lydieTotalComm + kpis.loizzaTotalComm)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>- Agent commissions owed</span><span style={{ color: '#ef4444', fontWeight: 600 }}>-{fmt(kpis.agentsOwed)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>+ Steve's personal deals</span><span style={{ color: 'var(--text2)', fontWeight: 600 }}>{fmt(kpis.steveCreatorIncome)}</span></div>
                <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#3b82f6', fontSize: 13 }}><span>= Net company income</span><span>{fmt(kpis.netIncome)}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
